import { useState, useRef, useEffect } from 'react';
import { api } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Brain, Loader2, CheckCircle, XCircle, AlertTriangle, Phone, DollarSign, Fuel, TrendingUp, Send, Zap, GraduationCap, PlayCircle } from 'lucide-react';

export default function AIDispatch({ user }) {
  const [driver, setDriver] = useState({ name: '', truckType: 'Semi Truck', currentCity: '', maxDeadheadMiles: '100' });
  const [load, setLoad] = useState({ brokerName: '', pickupCity: '', dropoffCity: '', miles: '', offerRate: '', weight: '', pickupTime: '', deliveryTime: '', brokerEmail: '' });
  const [aiResult, setAiResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [negotiating, setNegotiating] = useState(false);
  const [emailDraft, setEmailDraft] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [inquiring, setInquiring] = useState(false);
  const [inquiryDraft, setInquiryDraft] = useState(null); // {subject, body}
  const [draftLoading, setDraftLoading] = useState(false);
  const [holdProgress, setHoldProgress] = useState(0); // 0-100 for the 3s hold indicator
  const holdTimerRef = useRef(null);
  const holdStartRef = useRef(null);
  const HOLD_DURATION_MS = 3000;

  // Practice scenarios (only fetched when user is in training mode)
  const [scenarios, setScenarios] = useState([]);
  useEffect(() => {
    if (!user?.training_mode) return;
    api.get('/training/scenarios/mine')
      .then(res => setScenarios(res.data.scenarios || []))
      .catch(() => {});
  }, [user?.training_mode]);

  const loadScenario = async (scen) => {
    setLoad({
      brokerName: scen.broker_name,
      brokerEmail: scen.broker_email,
      pickupCity: `${scen.pickup_city}, ${scen.pickup_state}`,
      dropoffCity: `${scen.dropoff_city}, ${scen.dropoff_state}`,
      miles: String(scen.miles),
      offerRate: String(scen.offer_rate),
      weight: String(scen.weight),
      pickupTime: scen.pickup_time || '',
      deliveryTime: '',
    });
    // Also line up the driver's truck type to match the scenario
    if (scen.truck_type) {
      setDriver(d => ({ ...d, truckType: scen.truck_type }));
    }
    toast.success(`Loaded practice scenario: ${scen.title}`);
    try {
      const res = await api.post(`/training/scenarios/${scen.id}/mark-complete`);
      if (res.data?.refreshed) {
        // All 3 done → backend auto-seeded a new cycle, refetch
        const fresh = await api.get('/training/scenarios/mine');
        setScenarios(fresh.data.scenarios || []);
        toast.success('🔄 New practice loads unlocked — fresh brokers & cities!');
      } else {
        setScenarios(prev => prev.map(s => s.id === scen.id ? { ...s, completed: true } : s));
      }
    } catch (_) {
      setScenarios(prev => prev.map(s => s.id === scen.id ? { ...s, completed: true } : s));
    }
  };

  const canFireAutoInquiry = () =>
    !!load.brokerEmail && !!load.pickupCity && !!load.dropoffCity && !inquiring && !draftLoading;

  const autoFireInquiry = async () => {
    if (!canFireAutoInquiry()) return;
    setDraftLoading(true);
    try {
      // 1) AI draft
      const draftRes = await api.post('/loadboard/inquiry-draft', {
        broker_name: load.brokerName,
        driver: driver,
        load_summary: {
          pickup_city: load.pickupCity, dropoff_city: load.dropoffCity,
          miles: load.miles, offer_rate: load.offerRate, weight: load.weight,
          pickup_time: load.pickupTime,
        },
      });
      setInquiryDraft({ subject: draftRes.data.subject, body: draftRes.data.body });
      // 2) Auto-send with the AI body (skip confirm — the 3-second hold IS the confirm)
      await api.post('/loadboard/inquire-broker', {
        broker_name: load.brokerName,
        broker_email: load.brokerEmail,
        subject: draftRes.data.subject,
        body: draftRes.data.body,
        load_summary: {
          pickup_city: load.pickupCity, dropoff_city: load.dropoffCity,
          miles: load.miles, offer_rate: load.offerRate, weight: load.weight,
          pickup_time: load.pickupTime,
        },
      });
      toast.success(`⚡ Auto-fired AI inquiry to ${load.brokerEmail}`);
      setInquiryDraft(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Auto-fire failed');
    } finally {
      setDraftLoading(false);
      setHoldProgress(0);
    }
  };

  const beginHold = () => {
    if (!canFireAutoInquiry()) return;
    if (holdTimerRef.current) return;
    holdStartRef.current = Date.now();
    holdTimerRef.current = setInterval(() => {
      const elapsed = Date.now() - holdStartRef.current;
      const pct = Math.min((elapsed / HOLD_DURATION_MS) * 100, 100);
      setHoldProgress(pct);
      if (elapsed >= HOLD_DURATION_MS) {
        clearInterval(holdTimerRef.current);
        holdTimerRef.current = null;
        autoFireInquiry();
      }
    }, 60);
  };

  const cancelHold = () => {
    if (holdTimerRef.current) {
      clearInterval(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    setHoldProgress(0);
  };

  // Global Enter-key press-and-hold detector (agent presses+holds Enter for 3s anywhere)
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Enter' && !e.repeat) {
        // Ignore Enter while user is typing in an input/textarea/select
        const tag = (e.target?.tagName || '').toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
        beginHold();
      }
    };
    const onKeyUp = (e) => {
      if (e.key === 'Enter') cancelHold();
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      cancelHold();
    };
  }, [load.brokerEmail, load.pickupCity, load.dropoffCity, load.brokerName, driver, inquiring, draftLoading]);

  const generateInquiryDraft = async () => {
    if (!load.brokerEmail) { toast.error('Enter the broker email first'); return; }
    if (!load.pickupCity || !load.dropoffCity) { toast.error('Fill pickup + dropoff'); return; }
    setDraftLoading(true);
    try {
      const res = await api.post('/loadboard/inquiry-draft', {
        broker_name: load.brokerName,
        driver: driver,
        load_summary: {
          pickup_city: load.pickupCity, dropoff_city: load.dropoffCity,
          miles: load.miles, offer_rate: load.offerRate, weight: load.weight,
          pickup_time: load.pickupTime,
        },
      });
      setInquiryDraft({ subject: res.data.subject, body: res.data.body });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI draft failed');
    } finally {
      setDraftLoading(false);
    }
  };

  const askBroker = async (opts = {}) => {
    if (!load.brokerEmail) { toast.error('Enter the broker email first'); return; }
    if (!load.pickupCity || !load.dropoffCity) { toast.error('Fill pickup + dropoff so the broker knows which load'); return; }
    if (!opts.skipConfirm && !window.confirm(`Send "Still available?" email to ${load.brokerName || 'the broker'} at ${load.brokerEmail}?`)) return;
    setInquiring(true);
    try {
      const payload = {
        broker_name: load.brokerName,
        broker_email: load.brokerEmail,
        load_summary: {
          pickup_city: load.pickupCity, dropoff_city: load.dropoffCity,
          miles: load.miles, offer_rate: load.offerRate, weight: load.weight,
          pickup_time: load.pickupTime,
        },
      };
      if (inquiryDraft) {
        payload.subject = inquiryDraft.subject;
        payload.body = inquiryDraft.body;
      }
      const res = await api.post('/loadboard/inquire-broker', payload);
      toast.success(`Email sent to ${res.data.recipient}`);
      setInquiryDraft(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Send failed');
    } finally {
      setInquiring(false);
    }
  };

  const isSemiAuto = user?.ai_mode === 'semi_autonomous';

  const analyzeLoad = async () => {
    if (!load.pickupCity || !load.dropoffCity) { toast.error('Enter pickup and dropoff cities'); return; }
    setLoading(true);
    setAiResult(null);
    try {
      const res = await api.post('/ai-dispatch/score-load', { driver, load, dispatcherRules: {} });
      setAiResult(res.data.aiResult);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI analysis failed');
    } finally { setLoading(false); }
  };

  const sendForApproval = async () => {
    setSending(true);
    try {
      await api.post('/ai-dispatch/approval', { driver, loadData: load, aiDecision: aiResult });
      toast.success('Load sent for approval!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally { setSending(false); }
  };

  const generateNegotiation = async () => {
    if (!load.brokerEmail) { toast.error('Enter the broker email first'); return; }
    setNegotiating(true);
    try {
      const res = await api.post('/ai-dispatch/negotiate', {
        broker_email: load.brokerEmail,
        load,
        counter_offer: aiResult?.recommendedCounterOffer || load.offerRate,
        tone: 'professional'
      });
      setEmailDraft(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI negotiation failed');
    } finally { setNegotiating(false); }
  };

  const sendNegotiationEmail = async () => {
    if (!emailDraft) return;
    setSendingEmail(true);
    try {
      await api.post('/ai-dispatch/send-negotiation', {
        broker_email: emailDraft.broker_email,
        subject: emailDraft.subject,
        body: emailDraft.email_draft
      });
      toast.success(`Email sent to ${emailDraft.broker_email}!`);
      setEmailDraft(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    } finally { setSendingEmail(false); }
  };

  const decisionColors = { ACCEPT: 'bg-green-100 text-green-800 border-green-300', COUNTER: 'bg-amber-100 text-amber-800 border-amber-300', REJECT: 'bg-red-100 text-red-800 border-red-300' };
  const riskColors = { LOW: 'text-green-600', MEDIUM: 'text-amber-600', HIGH: 'text-red-600' };

  return (
    <div className="space-y-6" data-testid="ai-dispatch-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
          <Brain className="w-8 h-8 text-purple-600" />
          AI Dispatch Agent
        </h1>
        <p className="text-slate-600 mt-1">AI analyzes loads, scores them, and writes your broker script. You approve before booking.</p>
      </div>

      {/* Training Mode Practice Scenarios Panel */}
      {user?.training_mode && scenarios.length > 0 && (
        <Card
          className="border-amber-300 bg-gradient-to-br from-amber-50 via-white to-orange-50"
          data-testid="practice-scenarios-panel"
        >
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-amber-600" />
              Practice Scenarios
              <span className="text-[11px] font-normal text-amber-800 bg-amber-100 px-2 py-0.5 rounded-full">
                {scenarios.filter(s => s.completed).length}/{scenarios.length} tried
              </span>
            </CardTitle>
            <p className="text-xs text-slate-600 mt-1">
              Pick a scenario to auto-fill the Load Details card. Send an inquiry or auto-book — every successful action counts toward graduation.
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {scenarios.map(scen => {
                const styles = {
                  emerald: { border: 'border-emerald-300', pill: 'text-emerald-700 bg-emerald-100', btn: 'bg-emerald-600 hover:bg-emerald-700' },
                  amber:   { border: 'border-amber-300',   pill: 'text-amber-700 bg-amber-100',     btn: 'bg-amber-600 hover:bg-amber-700' },
                  rose:    { border: 'border-rose-300',    pill: 'text-rose-700 bg-rose-100',       btn: 'bg-rose-600 hover:bg-rose-700' },
                }[scen.difficulty_color] || { border: 'border-slate-300', pill: 'text-slate-700 bg-slate-100', btn: 'bg-slate-600 hover:bg-slate-700' };
                return (
                  <div
                    key={scen.id}
                    className={`border-2 rounded-lg p-3 space-y-2 bg-white hover:shadow-md transition ${
                      scen.completed ? 'border-slate-200 opacity-80' : styles.border
                    }`}
                    data-testid={`scenario-card-${scen.key}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded ${styles.pill}`}>
                        {scen.difficulty}
                      </span>
                      {scen.completed && (
                        <span className="text-[10px] text-emerald-700 flex items-center gap-0.5">
                          <CheckCircle className="w-3 h-3" /> Loaded
                        </span>
                      )}
                    </div>
                    <h4 className="text-sm font-bold text-slate-900 leading-tight">{scen.title}</h4>
                    <div className="text-[11px] text-slate-600 space-y-0.5">
                      <div className="font-semibold text-slate-700">🚛 {scen.truck_type}</div>
                      <div>📍 {scen.pickup_city}, {scen.pickup_state} → {scen.dropoff_city}, {scen.dropoff_state}</div>
                      <div>{scen.miles} mi · ${scen.offer_rate} · {scen.weight?.toLocaleString()} lbs</div>
                    </div>
                    <p className="text-[11px] text-slate-500 italic border-l-2 border-slate-200 pl-2">
                      {scen.coach_notes}
                    </p>
                    <div className="text-[10px] text-emerald-700 font-semibold">
                      🎯 {scen.target_action}
                    </div>
                    <Button
                      size="sm"
                      className={`w-full text-xs text-white ${styles.btn}`}
                      onClick={() => loadScenario(scen)}
                      data-testid={`load-scenario-${scen.key}`}
                    >
                      <PlayCircle className="w-3 h-3 mr-1" />
                      Load Scenario
                    </Button>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Driver Info */}
        <Card data-testid="driver-info-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Driver / Truck Info</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Driver Name</Label><Input placeholder="John Smith" value={driver.name} onChange={e => setDriver({...driver, name: e.target.value})} data-testid="ai-driver-name" /></div>
              <div>
                <Label className="text-xs">Truck Type</Label>
                <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={driver.truckType} onChange={e => setDriver({...driver, truckType: e.target.value})} data-testid="ai-truck-type">
                  <option>Semi Truck</option>
                  <option>Box Truck</option>
                  <option>Cargo Van</option>
                  <option>Sprinter Van</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Current City</Label><Input placeholder="Dallas, TX" value={driver.currentCity} onChange={e => setDriver({...driver, currentCity: e.target.value})} data-testid="ai-current-city" /></div>
              <div><Label className="text-xs">Max Deadhead (mi)</Label><Input type="number" value={driver.maxDeadheadMiles} onChange={e => setDriver({...driver, maxDeadheadMiles: e.target.value})} data-testid="ai-deadhead" /></div>
            </div>
          </CardContent>
        </Card>

        {/* Load Info — hover-and-hold 3s to auto-fire AI inquiry */}
        <Card
          data-testid="load-info-card"
          onMouseEnter={beginHold}
          onMouseLeave={cancelHold}
          className={holdProgress > 0 ? 'ring-2 ring-emerald-400 relative overflow-hidden' : 'relative'}
        >
          {holdProgress > 0 && (
            <div
              className="absolute top-0 left-0 h-1 bg-gradient-to-r from-emerald-500 to-purple-500 transition-all"
              style={{ width: `${holdProgress}%` }}
              data-testid="hold-progress-bar"
            />
          )}
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Load Details</CardTitle>
              {canFireAutoInquiry() && (
                <span className="text-[10px] uppercase tracking-widest text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded flex items-center gap-1" data-testid="auto-fire-hint">
                  <Zap className="w-3 h-3" />
                  Hover 3s or hold Enter to auto-fire
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Broker Name</Label><Input placeholder="XPO Logistics" value={load.brokerName} onChange={e => setLoad({...load, brokerName: e.target.value})} data-testid="ai-broker-name" /></div>
              <div><Label className="text-xs">Offer Rate ($)</Label><Input type="number" placeholder="2200" value={load.offerRate} onChange={e => setLoad({...load, offerRate: e.target.value})} data-testid="ai-offer-rate" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Pickup City</Label><Input placeholder="Dallas, TX" value={load.pickupCity} onChange={e => setLoad({...load, pickupCity: e.target.value})} data-testid="ai-pickup-city" /></div>
              <div><Label className="text-xs">Dropoff City</Label><Input placeholder="Atlanta, GA" value={load.dropoffCity} onChange={e => setLoad({...load, dropoffCity: e.target.value})} data-testid="ai-dropoff-city" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Miles</Label><Input type="number" placeholder="780" value={load.miles} onChange={e => setLoad({...load, miles: e.target.value})} data-testid="ai-miles" /></div>
              <div><Label className="text-xs">Weight (lbs)</Label><Input type="number" placeholder="20000" value={load.weight} onChange={e => setLoad({...load, weight: e.target.value})} data-testid="ai-weight" /></div>
              <div><Label className="text-xs">Pickup Time</Label><Input placeholder="6:00 AM" value={load.pickupTime} onChange={e => setLoad({...load, pickupTime: e.target.value})} data-testid="ai-pickup-time" /></div>
            </div>
            <div>
              <Label className="text-xs">Broker Email <span className="text-slate-400 font-normal">(for one-click inquiry + AI negotiate)</span></Label>
              <Input placeholder="broker@company.com" value={load.brokerEmail} onChange={e => setLoad({...load, brokerEmail: e.target.value})} data-testid="ai-broker-email" />
              {load.brokerEmail && (load.pickupCity || load.dropoffCity) && (
                <>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <Button
                      size="sm"
                      onClick={() => askBroker()}
                      disabled={inquiring || draftLoading}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                      data-testid="ask-broker-btn"
                    >
                      {inquiring ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                      Quick Send · Still Available?
                    </Button>
                    <Button
                      size="sm"
                      onClick={generateInquiryDraft}
                      disabled={inquiring || draftLoading}
                      className="bg-purple-600 hover:bg-purple-700 text-white text-xs"
                      data-testid="ai-boost-inquiry-btn"
                    >
                      {draftLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Brain className="w-3 h-3 mr-1" />}
                      AI Boost · Personalize
                    </Button>
                  </div>
                  {inquiryDraft && (
                    <div className="mt-3 bg-purple-50 border border-purple-200 rounded-lg p-3 space-y-2" data-testid="ai-inquiry-draft">
                      <p className="text-[10px] uppercase tracking-widest text-purple-700 font-bold">AI Draft — edit before sending</p>
                      <Input
                        value={inquiryDraft.subject}
                        onChange={e => setInquiryDraft(d => ({ ...d, subject: e.target.value }))}
                        className="text-xs"
                        data-testid="ai-inquiry-subject"
                      />
                      <textarea
                        value={inquiryDraft.body}
                        onChange={e => setInquiryDraft(d => ({ ...d, body: e.target.value }))}
                        rows={9}
                        className="w-full rounded-md border border-purple-200 px-3 py-2 text-sm bg-white"
                        data-testid="ai-inquiry-body"
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => askBroker({ skipConfirm: true })}
                          disabled={inquiring}
                          className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                          data-testid="send-ai-inquiry-btn"
                        >
                          {inquiring ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                          Send Personalized Email
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setInquiryDraft(null)}
                          disabled={inquiring}
                          className="text-xs"
                        >
                          Discard
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Analyze Button */}
      <Button className="w-full py-6 text-lg bg-purple-600 hover:bg-purple-700" onClick={analyzeLoad} disabled={loading} data-testid="analyze-load-btn">
        {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> AI Analyzing...</> : <><Brain className="w-5 h-5 mr-2" /> Analyze Load</>}
      </Button>

      {/* AI Result */}
      {aiResult && (
        <Card className="border-2 border-purple-200" data-testid="ai-result-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Brain className="w-5 h-5 text-purple-600" />
                AI Recommendation
              </CardTitle>
              <div className={`px-4 py-2 rounded-lg border-2 font-bold text-lg ${decisionColors[aiResult.decision] || 'bg-slate-100'}`} data-testid="ai-decision">
                {aiResult.decision === 'ACCEPT' && <CheckCircle className="w-5 h-5 inline mr-1" />}
                {aiResult.decision === 'REJECT' && <XCircle className="w-5 h-5 inline mr-1" />}
                {aiResult.decision === 'COUNTER' && <AlertTriangle className="w-5 h-5 inline mr-1" />}
                {aiResult.decision}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Score & Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="bg-purple-50 rounded-lg p-3 text-center">
                <p className="text-xs text-slate-500">Score</p>
                <p className="text-2xl font-bold text-purple-600">{aiResult.score}<span className="text-sm">/100</span></p>
              </div>
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <p className="text-xs text-slate-500">Rate/Mile</p>
                <p className="text-2xl font-bold text-blue-600">${aiResult.ratePerMile?.toFixed(2)}</p>
              </div>
              {aiResult.recommendedCounterOffer && (
                <div className="bg-amber-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-500">Counter Offer</p>
                  <p className="text-2xl font-bold text-amber-600">${aiResult.recommendedCounterOffer}</p>
                </div>
              )}
              {aiResult.fuelEstimate > 0 && (
                <div className="bg-orange-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-500">Fuel Est.</p>
                  <p className="text-2xl font-bold text-orange-600">${aiResult.fuelEstimate?.toFixed(0)}</p>
                </div>
              )}
              <div className="bg-slate-50 rounded-lg p-3 text-center">
                <p className="text-xs text-slate-500">Risk</p>
                <p className={`text-2xl font-bold ${riskColors[aiResult.riskLevel]}`}>{aiResult.riskLevel}</p>
              </div>
            </div>

            {/* Reason */}
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-sm font-medium text-slate-700 mb-1">Analysis:</p>
              <p className="text-sm text-slate-600">{aiResult.reason}</p>
            </div>

            {/* Broker Script */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4" data-testid="broker-script">
              <div className="flex items-center gap-2 mb-2">
                <Phone className="w-4 h-4 text-green-600" />
                <p className="text-sm font-bold text-green-800">Broker Script (say this on the phone):</p>
              </div>
              <p className="text-sm text-green-900 italic leading-relaxed">"{aiResult.brokerScript}"</p>
            </div>

            {/* Approval Notice */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-blue-600 shrink-0" />
              <div>
                <p className="text-sm font-medium text-blue-800">Human Approval Required</p>
                <p className="text-xs text-blue-600">AI cannot book this load. Send for admin/broker approval before proceeding.</p>
              </div>
            </div>

            {/* Send for Approval */}
            <Button className="w-full bg-green-600 hover:bg-green-700" onClick={sendForApproval} disabled={sending} data-testid="send-approval-btn">
              <Send className="w-4 h-4 mr-2" />
              {sending ? 'Sending...' : 'Send Load for Approval'}
            </Button>

            {/* Phase 2: AI Negotiate (Semi-Autonomous only) */}
            {isSemiAuto && (
              <div className="border-t pt-4 space-y-3" data-testid="phase2-section">
                <div className="flex items-center gap-2">
                  <Brain className="w-5 h-5 text-purple-600" />
                  <p className="text-sm font-bold text-purple-800">Phase 2: AI Auto-Negotiate</p>
                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">Semi-Auto Mode</span>
                </div>
                
                {!load.brokerEmail && (
                  <p className="text-sm text-amber-600">Enter the broker's email in the Load Details to enable AI negotiation.</p>
                )}
                
                {load.brokerEmail && !emailDraft && (
                  <Button className="w-full bg-purple-600 hover:bg-purple-700" onClick={generateNegotiation} disabled={negotiating} data-testid="negotiate-btn">
                    <Brain className="w-4 h-4 mr-2" />
                    {negotiating ? 'AI Writing Email...' : `AI Negotiate with ${load.brokerName || 'Broker'}`}
                  </Button>
                )}

                {emailDraft && (
                  <div className="space-y-3">
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                      <p className="text-xs font-bold text-purple-700 mb-1">To: {emailDraft.broker_email}</p>
                      <p className="text-xs text-purple-600 mb-2">Subject: {emailDraft.subject}</p>
                      <textarea
                        className="w-full rounded-md border border-purple-200 px-3 py-2 text-sm bg-white min-h-[100px]"
                        value={emailDraft.email_draft}
                        onChange={e => setEmailDraft({...emailDraft, email_draft: e.target.value})}
                        data-testid="email-draft-textarea"
                      />
                      <p className="text-xs text-slate-500 mt-1">Edit the message above if needed, then send with 1 click.</p>
                    </div>
                    <div className="flex gap-2">
                      <Button className="flex-1 bg-green-600 hover:bg-green-700" onClick={sendNegotiationEmail} disabled={sendingEmail} data-testid="send-negotiation-btn">
                        <Send className="w-4 h-4 mr-2" />
                        {sendingEmail ? 'Sending...' : 'Send to Broker'}
                      </Button>
                      <Button variant="outline" onClick={() => setEmailDraft(null)}>Discard</Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {!isSemiAuto && (
              <p className="text-xs text-slate-400 text-center">Want AI to negotiate directly? Ask your admin to upgrade you to Semi-Auto mode in User Management.</p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
